import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FiArrowLeft, FiHeart, FiMessageCircle, FiShare2, FiBookmark, FiVolume2, FiVolumeX } from 'react-icons/fi';
import { MdVerified } from 'react-icons/md';
import { useAuth } from '../../context/AuthContext';
import { postsAPI, commentsAPI, favoritesAPI, reactionsAPI } from '../../services/api';
import { CATEGORIES } from '../../utils/constants';
import LoginPrompt from '../../components/LoginPrompt/LoginPrompt';
import { resolveMediaUrl } from '../../utils/media';
import './Reels.css';

const ReelItem = ({ reel, isActive, onInteraction, isMuted, onToggleMute }) => {
  const videoRef = useRef(null);
  const [userReaction, setUserReaction] = useState(null);
  const [reactionCounts, setReactionCounts] = useState({
    like: 0,
    love: 0,
    smart: 0,
    wow: 0,
    think: 0
  });
  const [isSaved, setIsSaved] = useState(false);
  const [showComments, setShowComments] = useState(false);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const { isAuthenticated } = useAuth();

  const slugify = (text) => {
    if (!text) return '';
    return text
      .toString()
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
  };

  const getReelSlug = () => {
    if (reel?.slug) return reel.slug;
    const base = slugify(reel?.title) || 'video';
    return reel?.id ? `${base}-${reel.id}` : base;
  };

  const category = CATEGORIES.find(cat => cat.id === reel.category);

  useEffect(() => {
    if (videoRef.current) {
      if (isActive) {
        videoRef.current.play().catch(() => {});
      } else {
        videoRef.current.pause();
        videoRef.current.currentTime = 0;
      }
    }
  }, [isActive]);

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.muted = isMuted;
    }
  }, [isMuted]);

  useEffect(() => {
    setReactionCounts({
      like: reel?.reactions?.like ?? 0,
      love: reel?.reactions?.love ?? reel?.love_count ?? 0,
      smart: reel?.reactions?.smart ?? 0,
      wow: reel?.reactions?.wow ?? 0,
      think: reel?.reactions?.think ?? 0
    });
  }, [reel]);

  useEffect(() => {
    let isMounted = true;
    if (!isAuthenticated || !reel?.id) return;

    (async () => {
      try {
        const [reactionRes, favRes] = await Promise.all([
          reactionsAPI.getUserReaction(reel.id),
          favoritesAPI.check(reel.id)
        ]);
        if (!isMounted) return;
        setUserReaction(reactionRes.data?.reactionType || null);
        setIsSaved(!!favRes.data?.isFavorited);
      } catch (e) {
        // ignore
      }
    })();

    return () => {
      isMounted = false;
    };
  }, [isAuthenticated, reel?.id]);

  const loadComments = async () => {
    try {
      const response = await commentsAPI.getByPost(reel.id);
      setComments(response.data || []);
    } catch (error) {
      console.error('Error loading comments:', error);
    }
  };

  const handleLike = () => {
    if (!isAuthenticated) {
      onInteraction();
      return;
    }
    (async () => {
      try {
        const res = await reactionsAPI.toggle(reel.id, 'love');
        setUserReaction(res.data?.userReaction || null);
        setReactionCounts(res.data?.reactions || {});
      } catch (e) {
        // ignore
      }
    })();
  };

  const handleSave = () => {
    if (!isAuthenticated) {
      onInteraction();
      return;
    }
    (async () => {
      try {
        if (isSaved) {
          const res = await favoritesAPI.remove(reel.id);
          setIsSaved(!!res.data?.isFavorited);
        } else {
          const res = await favoritesAPI.add(reel.id);
          setIsSaved(!!res.data?.isFavorited);
        }
      } catch (e) {
        // ignore
      }
    })();
  };

  const handleComment = () => {
    if (!isAuthenticated) {
      onInteraction();
      return;
    }
    setShowComments(true);
    loadComments();
  };

  const handleShare = async () => {
    const url = `${window.location.origin}/video/${getReelSlug()}`;
    if (navigator.share) {
      try {
        await navigator.share({ title: reel.title, url });
      } catch (e) {}
    } else {
      navigator.clipboard.writeText(url);
    }
  };

  const handleAddComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim() || !isAuthenticated) return;

    try {
      await commentsAPI.create({ postId: reel.id, content: newComment });
      setNewComment('');
      loadComments();
    } catch (error) {
      console.error('Error adding comment:', error);
    }
  };

  const formatCount = (count) => {
    if (count >= 1000000) return `${(count / 1000000).toFixed(1)}M`;
    if (count >= 1000) return `${(count / 1000).toFixed(1)}K`;
    return count?.toString() || '0';
  };

  return (
    <div className="reel-item">
      <video
        ref={videoRef}
        src={reel.video_url}
        className="reel-video"
        loop
        playsInline
        muted={isMuted}
        poster={reel.image}
        onClick={onToggleMute}
      />

      {/* Overlay gradient */}
      <div className="reel-overlay" />

      {/* Mute indicator */}
      <button className="mute-indicator" onClick={onToggleMute}>
        {isMuted ? <FiVolumeX /> : <FiVolume2 />}
      </button>

      {/* Actions */}
      <div className="reel-actions">
        <button 
          className={`reel-action ${userReaction === 'love' ? 'active' : ''}`}
          onClick={handleLike}
        >
          <FiHeart />
          <span>{formatCount(reactionCounts.love ?? reel.likes)}</span>
        </button>

        <button className="reel-action" onClick={handleComment}>
          <FiMessageCircle />
          <span>{formatCount(reel.comments)}</span>
        </button>

        <button 
          className={`reel-action ${isSaved ? 'active' : ''}`}
          onClick={handleSave}
        >
          <FiBookmark />
        </button>

        <button className="reel-action" onClick={handleShare}>
          <FiShare2 />
        </button>
      </div>

      {/* Info */}
      <div className="reel-info">
        <div className="reel-author">
          <img 
            src={resolveMediaUrl(reel.author?.avatar) || `https://i.pravatar.cc/40?u=${reel.author_id}`}
            alt={reel.author?.name}
          />
          <span className="author-name">
            {reel.author?.name}
            {reel.author?.verification_level && reel.author.verification_level !== 'none' && (
              <MdVerified
                style={{
                  marginLeft: 6,
                  verticalAlign: 'middle',
                  color: reel.author.badge_color || 'currentColor'
                }}
                title={reel.author.badge_label || 'Cuenta verificada'}
              />
            )}
          </span>
          {category && (
            <span className="reel-category" style={{ backgroundColor: category.color }}>
              {category.name}
            </span>
          )}
        </div>
        <p className="reel-title">{reel.title}</p>
        {reel.description && (
          <p className="reel-description">{reel.description}</p>
        )}
      </div>

      {/* Comments Panel */}
      {showComments && (
        <div className="reel-comments-panel" onClick={(e) => e.stopPropagation()}>
          <div className="comments-header">
            <h4>Comentarios ({comments.length})</h4>
            <button onClick={() => setShowComments(false)}>✕</button>
          </div>
          
          <div className="comments-list">
            {comments.length === 0 ? (
              <p className="no-comments">Sé el primero en comentar</p>
            ) : (
              comments.map(comment => (
                <div key={comment.id} className="comment-item">
                  <img 
                    src={comment.user_avatar || `https://i.pravatar.cc/32?u=${comment.user_id}`}
                    alt={comment.user_name}
                  />
                  <div className="comment-content">
                    <strong>
                      {comment.user_name}
                      {comment.user_verification_level && comment.user_verification_level !== 'none' && (
                        <MdVerified
                          style={{
                            marginLeft: 6,
                            verticalAlign: 'middle',
                            color: comment.user_badge_color || 'currentColor'
                          }}
                          title={comment.user_badge_label || 'Cuenta verificada'}
                        />
                      )}
                    </strong>
                    <p>{comment.content || comment.text}</p>
                  </div>
                </div>
              ))
            )}
          </div>

          <form className="comment-form" onSubmit={handleAddComment}>
            <input
              type="text"
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              placeholder="Escribe un comentario..."
            />
            <button type="submit">Enviar</button>
          </form>
        </div>
      )}
    </div>
  );
};

const Reels = () => {
  const navigate = useNavigate();
  const { reelId } = useParams();
  const containerRef = useRef(null);
  const [reels, setReels] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);
  const [isMuted, setIsMuted] = useState(true);

  const scrollToIndex = useCallback((index) => {
    if (containerRef.current && index >= 0) {
      containerRef.current.scrollTo({
        top: index * window.innerHeight,
        behavior: 'smooth'
      });
    }
  }, []);

  useEffect(() => {
    const loadReels = async () => {
      try {
        const response = await postsAPI.getAll({ type: 'video-reel' });
        const loadedReels = response.data.posts || [];
        setReels(loadedReels);
        
        // Si hay un reelId en la URL, encontrar su índice
        if (reelId && loadedReels.length > 0) {
          const targetIndex = loadedReels.findIndex(r => r.id.toString() === reelId);
          if (targetIndex >= 0) {
            setCurrentIndex(targetIndex);
            // Scroll al reel después de cargar
            setTimeout(() => scrollToIndex(targetIndex), 100);
          }
        }
      } catch (error) {
        console.error('Error loading reels:', error);
      } finally {
        setLoading(false);
      }
    };
    
    loadReels();
  }, [reelId, scrollToIndex]);

  const handleScroll = useCallback((e) => {
    const container = containerRef.current;
    if (!container) return;

    const scrollTop = container.scrollTop;
    const itemHeight = window.innerHeight;
    const newIndex = Math.round(scrollTop / itemHeight);

    if (newIndex !== currentIndex && newIndex >= 0 && newIndex < reels.length) {
      setCurrentIndex(newIndex);
    }
  }, [currentIndex, reels.length]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        scrollToIndex(currentIndex + 1);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        scrollToIndex(currentIndex - 1);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentIndex, scrollToIndex]);

  if (loading) {
    return (
      <div className="reels-loading">
        <div className="spinner"></div>
        <p>Cargando reels...</p>
      </div>
    );
  }

  return (
    <div className="reels-page">
      <button className="reels-back" onClick={() => navigate(-1)}>
        <FiArrowLeft />
      </button>

      <div 
        ref={containerRef}
        className="reels-container"
        onScroll={handleScroll}
      >
        {reels.map((reel, index) => (
          <ReelItem
            key={reel.id}
            reel={reel}
            isActive={index === currentIndex}
            onInteraction={() => setShowLoginPrompt(true)}
            isMuted={isMuted}
            onToggleMute={() => setIsMuted(!isMuted)}
          />
        ))}
      </div>

      {/* Progress indicators */}
      <div className="reels-progress">
        {reels.map((_, index) => (
          <div 
            key={index}
            className={`progress-dot ${index === currentIndex ? 'active' : ''}`}
            onClick={() => scrollToIndex(index)}
          />
        ))}
      </div>

      {showLoginPrompt && (
        <LoginPrompt 
          message="Inicia sesión para interactuar con los reels"
          onClose={() => setShowLoginPrompt(false)}
        />
      )}
    </div>
  );
};

export default Reels;
